import { ReactElement } from 'react';
import SVG from 'react-inlinesvg';
import cx from 'classnames';
import { warningDealStatuses } from 'consts/ooc';
import { DealStatus, Line } from 'components/common/types/Deal.types';
import { getFormattedDateTime } from 'modules/I18N';
import copyIcon from 'assets/icons/copy.svg';
import { DirectSalesLine } from 'components/common/types/DirectSalesCampaign.types';
import { DealLinesStatus } from '../DealLinesIcon/DealLinesIcon.types';
import DealLinesIcon from '../DealLinesIcon';

const getDealLinesIconStatus = (
  line: Line | DirectSalesLine,
  bookingStatusCode: DealStatus,
  hasOutOfChargeEnabled: boolean,
  isBookingStatusFailed: boolean,
): {
  dealLinesIconStatus: DealLinesStatus | false | null;
  showOOCWarning?: boolean;
  isLineWithOutOfChargedFrames?: boolean;
} => {
  if (!hasOutOfChargeEnabled)
    return {
      dealLinesIconStatus: null,
      isLineWithOutOfChargedFrames: false,
    };

  const isLineWithOutOfChargedFrames = line?.dischargedFrameIds?.length > 0;
  const showOOCWarning = warningDealStatuses.includes(bookingStatusCode) && isLineWithOutOfChargedFrames;
  const dealLinesIconStatus =
    ((isBookingStatusFailed || line.bookingStatusCode === DealStatus.FAILED) &&
      DealLinesStatus.BOOKING_STATUS_FAILED) ||
    (showOOCWarning && DealLinesStatus.LINE_WITH_OUT_OF_CHARGE_FRAMES);

  return {
    showOOCWarning,
    dealLinesIconStatus,
  };
};

type DealLineProps = {
  line: Line | DirectSalesLine;
  bookingStatusCode: DealStatus;
  isMultiLineDeal: boolean;
  isBookingStatusFailed: boolean;
  isLineDuplicationDisabled: boolean;
  hasOutOfChargeEnabled: boolean;
  onMouseEnter: (id: string) => void;
  onSelectLine: (id: string) => void;
  onDuplicateLine: (id: string) => void;
};

export const DealLine = ({
  line,
  bookingStatusCode,
  hasOutOfChargeEnabled,
  isMultiLineDeal,
  isBookingStatusFailed,
  isLineDuplicationDisabled,
  onMouseEnter,
  onSelectLine,
  onDuplicateLine,
}: DealLineProps): ReactElement => {
  const { dealLinesIconStatus, showOOCWarning } = getDealLinesIconStatus(
    line,
    bookingStatusCode,
    hasOutOfChargeEnabled,
    isBookingStatusFailed,
  );
  const lineFailed = isMultiLineDeal ? line.bookingStatusCode === DealStatus.FAILED : isBookingStatusFailed;

  return (
    <div
      data-test-id="deal-line"
      className={cx('group flex justify-between items-center w-full space-x-2 rounded-md', {
        'text-neutral': line.terminated,
        'bg-pinkRed-opacity-20 border border-pinkRed-500': lineFailed,
        'border border-transparent hover:bg-neutral-100 hover:border-neutral-300': !lineFailed,
        'bg-yellow-opacity-20 border border-yellow-300 hover:bg-yellow-opacity-20 hover:border-yellow-300':
          showOOCWarning,
      })}
      onMouseEnter={() => onMouseEnter(line.id)}
    >
      <button
        data-test-id="line"
        type="button"
        className="flex items-baseline p-2"
        onClick={() => onSelectLine(line.id)}
      >
        <p className="w-24 mr-2 text-left truncate sub-header-base">{line.name}</p>
        <p className="body-base">
          {getFormattedDateTime(new Date(line.createdDate), {
            dateStyle: 'medium',
          })}
        </p>
      </button>
      {line.terminated ? <p className="body-base text-neutral-900">T</p> : null}
      {dealLinesIconStatus ? <DealLinesIcon status={dealLinesIconStatus} /> : null}
      <div className="p-2 invisible group-hover:visible">
        <button
          type="button"
          data-test-id="deal-line-duplicate-button"
          onClick={() => onDuplicateLine(line.id)}
          disabled={isLineDuplicationDisabled}
          className="disabled:opacity-25 disabled:text-neutral-900"
        >
          <SVG src={copyIcon} className="text-primary" />
        </button>
      </div>
    </div>
  );
};

export default DealLine;
